import React from 'react';
import { EXPORT_FETCH_CONCURRENCY } from 'constants/index';
import { estimateTimeLeftMs, formatTimeLeft } from 'utils/eta';
import * as css from './style';

interface Props {
  title: string,
  completed: number,
  total: number,
  unit: string,
  elapsedMs: number,
  showEstimate?: boolean,
}

/**
 * Export status panel. The bar measures units of work finished against the total
 * the export has to do, so it moves on real progress rather than on a timer.
 *
 * Phase-agnostic on purpose: an export can spend most of its time in a stage that
 * is not paging -- the file report fetches download history after paging -- and a
 * panel that only knew about pages had to either sit at 100% or lie about what it
 * was doing.
 *
 * @param {Props.title} title what the export is doing right now
 * @param {Props.completed} completed units of work finished
 * @param {Props.total} total units of work in this phase
 * @param {Props.unit} unit plural noun for the unit of work
 * @param {Props.elapsedMs} elapsedMs wall clock since this phase started
 * @param {Props.showEstimate} showEstimate offer a time left estimate
 */
const ExportProgress = ({ title, completed, total, unit, elapsedMs, showEstimate = true }: Props) => {
  if (total <= 0) return null;

  const percent = Math.min(100, Math.round((completed / total) * 100));
  const timeLeft = showEstimate
    ? formatTimeLeft(estimateTimeLeftMs(completed, total, elapsedMs, EXPORT_FETCH_CONCURRENCY))
    : '';

  return (
    <css.Container>
      <css.Title>{title}</css.Title>
      <css.ProgressBarContainer grid={2} columns="auto|46px" gap="10px">
        <css.Progress>
          <css.ProgressBar xWidth={`${percent}%`} />
        </css.Progress>
        <css.ProgressText>{`${percent}%`}</css.ProgressText>
      </css.ProgressBarContainer>
      <css.Detail>
        {`${completed.toLocaleString()} of ${total.toLocaleString()} ${unit}`}
        {timeLeft ? ` — ${timeLeft}` : ''}
      </css.Detail>
    </css.Container>
  );
};

export default ExportProgress;
